import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  ActiveSpecialOffer,
  OfferContentFeatures,
  OfferContentDisclaimers,
  OfferContentTitleDisclaimer,
  OfferContentCTA,
} from '../../store';
import styles from './index.module.scss';
import { OfferContent } from '~/common/models';
import {
  Button,
  SvgIcon,
  TooltipDisclaimer,
} from '~/common/components/ui-elements';
import cn from 'classnames';
import { useIsMobile } from '~/common/hooks/use-is-mobile';

export const OfferLandingContent = () => {
  const offer = useRecoilValue(ActiveSpecialOffer);
  const offerContent = useRecoilValue(OfferContentFeatures);
  const offerDisclaimers = useRecoilValue(OfferContentDisclaimers);
  const offerTitleDisclaimer = useRecoilValue(OfferContentTitleDisclaimer);
  const { containerRef, isMobile } = useIsMobile();

  const offerContentFloatingCta = useRecoilValue(
    OfferContentCTA('floating-ctas')
  );
  const offerContentSubFloatingCta = useRecoilValue(
    OfferContentCTA('sub-floating-ctas')
  );

  const offerContentBBFloatingCta = useRecoilValue(
    OfferContentCTA('bluebox-ctas')
  );

  const offerContentBBSubFloatingCta = useRecoilValue(
    OfferContentCTA('sub-bluebox-ctas')
  );

  function removeTextNodeParagraph(html: string): string {
    return html.replace(/<p class=["']text-node["']>(.*?)<\/p>/gi, '$1').trim();
  }

  return !!offer ? (
    <div ref={containerRef} className={styles.offerLandingPage}>
      <div className={styles.offerContentContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.contentHeading}>
            {offerTitleDisclaimer?.contentType ===
              'DISCLAIMER-CONTENT-TITLE' && (
              <TooltipDisclaimer
                customContent
                disclaimer={offerTitleDisclaimer.content}
                triggerClassName={styles.disclaimerTrigger}
              >
                <h1
                  dangerouslySetInnerHTML={{
                    __html: removeTextNodeParagraph(offer?.contentTitle),
                  }}
                />
              </TooltipDisclaimer>
            )}
          </div>

          {offerContent.map((content, index) => (
            <RenderOfferContent key={`CONTENT-${index}`} {...content} />
          ))}
          {!isMobile() && (
            <div className={styles.ctaWrapper}>
              <div className={styles.offerFloatingCta}>
                {offerContentFloatingCta.map((content, index) => (
                  <RenderOfferFloatingCta
                    key={`FLOATING-CTA-${index}`}
                    content={content}
                    section="floating-ctas"
                  />
                ))}
              </div>
            </div>
          )}

          {!isMobile() && offerContentSubFloatingCta.length > 0 && (
            <div className={styles.subCtaWrapper}>
              <div className={styles.offerFloatingCta}>
                {offerContentSubFloatingCta.map((content, index) => (
                  <RenderOfferFloatingCta
                    key={`SUB-FLOATING-CTA-${index}`}
                    content={content}
                    section="sub-floating-ctas"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.blueboxWrapper}>
          <div className={styles.bluebox}>
            <div className={styles.ctaWrapper}>
              {offerContentBBFloatingCta.map((content, index) => (
                <RenderOfferFloatingCta
                  key={`BLUEBOX-CTA-${index}`}
                  content={content}
                  section="bluebox-ctas"
                />
              ))}
            </div>
            {offerContentBBSubFloatingCta.map((content, index) => (
              <RenderOfferFloatingCta
                key={`SUB-BLUEBOX-CTA-${index}`}
                content={content}
                section="sub-bluebox-ctas"
              />
            ))}
          </div>
        </div>
      </div>

      {offerDisclaimers.map((content, index) => (
        <RenderOfferContent key={`DISCLAIMER-${index}`} {...content} />
      ))}
    </div>
  ) : (
    <div>Offer not found</div>
  );
};

export const RenderOfferContent = (content: OfferContent) => {
  if (content.contentType === 'PARAGRAPH') {
    return (
      <div
        className={styles.paragraph}
        dangerouslySetInnerHTML={{ __html: content.content }}
      />
    );
  }

  if (content.contentType === 'DISCLAIMER-CONTENT') {
    return (
      <div
        className={styles.bottomDisclaimer}
        dangerouslySetInnerHTML={{ __html: content.content }}
      />
    );
  }

  if (content.contentType === 'LIST') {
    return (
      <div className={styles.listContent}>
        <ul>
          {content.content.map((item, index) => (
            <li key={index}>
              <div dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
};

interface RenderOfferFloatingCtaProps {
  content: OfferContent;
  section:
    | 'floating-ctas'
    | 'bluebox-ctas'
    | 'sub-floating-ctas'
    | 'sub-bluebox-ctas';
}
export const RenderOfferFloatingCta = ({
  content,
  section,
}: RenderOfferFloatingCtaProps) => {
  const renderCta = React.useCallback(() => {
    if (content.contentType === 'CTA') {
      if (section === 'floating-ctas') {
        return (
          <Button
            buttonSize="large"
            buttonWidth="full-mobile-only"
            className={styles.floatingCta}
            href={content.content.link}
            {...{
              type: content.content.type === 'primary' ? 'primary' : 'bordered',
            }}
          >
            {content.content.text}
          </Button>
        );
      }

      if (section === 'sub-floating-ctas') {
        return (
          <a href={content.content.link} className={styles.subFloatingCta}>
            <SvgIcon type="chevronRight" size={0.8} color="#043A9C" />
            <span>{content.content.text}</span>
          </a>
        );
      }

      if (section === 'bluebox-ctas') {
        return (
          <Button
            buttonSize="large"
            buttonWidth="full-width"
            className={cn(styles[`bluebox_${content.content.type}`])}
            href={content.content.link}
            type="borderedSecondary"
          >
            {content.content.text}
          </Button>
        );
      }

      if (section === 'sub-bluebox-ctas') {
        return (
          <>
            <a href={content.content.link} className={styles.subBlueBoxCta}>
              <SvgIcon type="chevronRight" size={0.8} color="#fff" />
              <span>{content.content.text}</span>
            </a>
            <div className={styles.subBlueboxDivider} />
          </>
        );
      }
    }

    return null;
  }, []);

  return renderCta();
};
